import React, { useEffect, useRef, useState } from "react";
import { Image, Pressable, StyleSheet, View, ViewProps } from "react-native";
import { match } from "ts-pattern";
import { Except } from "type-fest";
import { colors, invariantColor, radii } from "../utils/design";
import { Icon } from "./Icon";
import { Text, TextVariant } from "./Text";

const styles = StyleSheet.create({
  avatar: {
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
  },
  hiddenImage: {
    position: "absolute",
    width: 0,
    height: 0,
    opacity: 0,
  },

  placeholder: {
    backgroundColor: colors.neutral[700],
    borderWidth: 1,
    borderColor: colors.neutral[300],
    borderStyle: "dashed",
  },

  avatars: {
    flexDirection: "row",
  },

  editAvatar: {
    position: "absolute",
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.primary[500],
    backgroundColor: colors.primary[900],
  },
  userEditAvatar: {
    right: -1,
    bottom: -1,
  },
  teamEditAvatar: {
    right: -10,
    bottom: -10,
  },
  orgEditAvatar: {
    right: -10,
    bottom: -10,
  },
});

const avatarColors = [
  colors.primary[500],
  colors.negative[300],
  colors.positive[300],
  colors.neutral[200],
  "#C497FF",
  "#97C1FF",
  "#97FFF9",
  "#FF97EE",
  invariantColor.white,
];

const getAvatarColor = (name: string) => {
  if (name.length === 0) {
    return avatarColors[0];
  }

  const charSum = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return avatarColors[charSum % avatarColors.length];
};

type AvatarImageProps = {
  url: string;
  updatedAt?: string;
};

/**
 * This component is used to avoid flickering when updatedAt change but the image is the same.
 * it render a second image hidden and when it's loaded it replace the first one.
 */
const AvatarImage: React.FC<AvatarImageProps> = ({ url, updatedAt }) => {
  const query = updatedAt != null ? `?updatedAt=${updatedAt}` : "";
  const uri = `${url}${query}`;
  const uriRef = useRef<string>(uri);

  const [backgroundLoadingUri, setBackgroundLoadingUri] = useState<string | null>(null);

  useEffect(() => {
    if (uriRef.current === uri) {
      return;
    }

    setBackgroundLoadingUri(uri);
  }, [uri]);

  return (
    <>
      <Image source={uriRef.current} style={styles.image} />
      {backgroundLoadingUri != null && (
        <Image
          source={backgroundLoadingUri}
          aria-hidden={true}
          style={styles.hiddenImage}
          onLoad={() => {
            uriRef.current = backgroundLoadingUri;
            setBackgroundLoadingUri(null);
          }}
        />
      )}
    </>
  );
};

type UserAvatarProps = {
  name: string;
  url: string | null;
  updatedAt?: string;
  size?: "extra-large" | "large" | "medium" | "regular" | "small";
  style?: ViewProps["style"];
};

export const UserAvatar = ({ name, url, updatedAt, size = "regular", style }: UserAvatarProps) => {
  const avatarColor = url == null ? getAvatarColor(name) : colors.neutral[900];

  const { avatarSize, variant } = match(size)
    .returnType<{ avatarSize: number; variant: TextVariant }>()
    .with("extra-large", () => ({ avatarSize: 64, variant: "title1" }))
    .with("large", () => ({ avatarSize: 48, variant: "title2" }))
    .with("medium", () => ({ avatarSize: 36, variant: "title3" }))
    .with("regular", () => ({ avatarSize: 24, variant: "bodyFlat" }))
    .with("small", () => ({ avatarSize: 16, variant: "miniFlat" }))
    .exhaustive();

  return (
    <View
      style={[
        styles.avatar,
        {
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarSize / 2,
          backgroundColor: avatarColor,
        },
        style,
      ]}
    >
      {url != null ? (
        <AvatarImage url={url} updatedAt={updatedAt} />
      ) : (
        <Text variant={variant} color={colors.neutral[900]}>
          {name[0]?.toUpperCase()}
        </Text>
      )}
    </View>
  );
};

type TeamAvatarProps = {
  name: string;
  url: string | null;
  updatedAt?: string;
  size?: "large" | "medium" | "regular";
  style?: ViewProps["style"];
};

export const TeamAvatar: React.FC<TeamAvatarProps> = ({
  name,
  url,
  updatedAt,
  size = "regular",
  style,
}) => {
  const { avatarSize, variant } = match(size)
    .returnType<{ avatarSize: number; variant: TextVariant }>()
    .with("large", () => ({ avatarSize: 48, variant: "smallFlat" }))
    .with("medium", () => ({ avatarSize: 36, variant: "miniFlat" }))
    .with("regular", () => ({ avatarSize: 24, variant: "miniAvatar" }))
    .exhaustive();

  return (
    <View
      style={[
        styles.avatar,
        {
          width: avatarSize,
          height: avatarSize,
          backgroundColor: colors.neutral[700],
          borderRadius: radii[4],
        },
        style,
      ]}
    >
      {url != null ? (
        <AvatarImage url={url} updatedAt={updatedAt} />
      ) : (
        <Text variant={variant} color={invariantColor.white}>
          {name.slice(0, 3).toUpperCase()}
        </Text>
      )}
    </View>
  );
};

type OrgAvatarProps = {
  name: string;
  url: string | null;
  updatedAt?: string;
  size?: "massive" | "large" | "medium" | "regular";
  style?: ViewProps["style"];
};

export const OrgAvatar: React.FC<OrgAvatarProps> = ({
  name,
  url,
  updatedAt,
  size = "regular",
  style,
}) => {
  const { avatarSize, variant } = match(size)
    .returnType<{ avatarSize: number; variant: TextVariant }>()
    .with("massive", () => ({ avatarSize: 80, variant: "massiveAvatar" }))
    .with("large", () => ({ avatarSize: 48, variant: "title2" }))
    .with("medium", () => ({ avatarSize: 36, variant: "title3" }))
    .with("regular", () => ({ avatarSize: 24, variant: "bodyFlat" }))
    .exhaustive();

  return (
    <View
      style={[
        styles.avatar,
        {
          width: avatarSize,
          height: avatarSize,
          backgroundColor: url == null ? colors.primary[500] : colors.neutral[900],
          borderRadius: size === "massive" ? radii[8] : radii[4],
        },
        style,
      ]}
    >
      {url != null ? (
        <AvatarImage url={url} updatedAt={updatedAt} />
      ) : (
        <Text variant={variant} color={colors.neutral[900]}>
          {name[0]?.toUpperCase()}
        </Text>
      )}
    </View>
  );
};

type IconAvatarProps = {
  name: IconName;
  color: string;
  size?: "large" | "medium" | "regular";
  style?: ViewProps["style"];
};

export const IconAvatar: React.FC<IconAvatarProps> = ({ name, color, size = "regular", style }) => {
  const { avatarSize, iconSize } = match(size)
    .returnType<{ avatarSize: number; iconSize: number }>()
    .with("large", () => ({ avatarSize: 48, iconSize: 24 }))
    .with("medium", () => ({ avatarSize: 36, iconSize: 24 }))
    .with("regular", () => ({ avatarSize: 24, iconSize: 16 }))
    .exhaustive();

  return (
    <View
      style={[
        styles.avatar,
        {
          width: avatarSize,
          height: avatarSize,
          backgroundColor: colors.neutral[700],
          borderRadius: radii[4],
        },
        style,
      ]}
    >
      <Icon name={name} size={iconSize} color={color} />
    </View>
  );
};

type PlaceholderAvatarProps = {
  name?: string;
  rounded?: boolean;
  size?: "extra-large" | "large" | "medium" | "regular" | "small";
  style?: ViewProps["style"];
};

export const PlaceholderAvatar: React.FC<PlaceholderAvatarProps> = ({
  name,
  rounded = false,
  size = "regular",
  style,
}) => {
  const { avatarSize, variant } = match(size)
    .returnType<{ avatarSize: number; variant: TextVariant }>()
    .with("extra-large", () => ({ avatarSize: 64, variant: "title1" }))
    .with("large", () => ({ avatarSize: 48, variant: "title2" }))
    .with("medium", () => ({ avatarSize: 36, variant: "title3" }))
    .with("regular", () => ({ avatarSize: 24, variant: "bodyFlat" }))
    .with("small", () => ({ avatarSize: 16, variant: "miniFlat" }))
    .exhaustive();

  return (
    <View
      style={[
        styles.avatar,
        styles.placeholder,
        {
          width: avatarSize,
          height: avatarSize,
          borderRadius: rounded ? avatarSize / 2 : radii[4],
        },
        style,
      ]}
    >
      {name != null && (
        <Text variant={variant} color={colors.neutral[200]}>
          {name[0]?.toUpperCase()}
        </Text>
      )}
    </View>
  );
};

type MultiUserAvatarProps = {
  avatars: Except<UserAvatarProps, "size" | "style">[];
  size?: UserAvatarProps["size"];
};

export const MultiUserAvatar: React.FC<MultiUserAvatarProps> = ({ avatars, size }) => {
  return (
    <View style={styles.avatars}>
      {avatars.map((avatar, index) => (
        <UserAvatar
          key={index}
          {...avatar}
          size={size}
          style={{
            marginLeft: index === 0 ? 0 : -6,
            borderWidth: 2,
            borderColor: colors.neutral[900],
          }}
        />
      ))}
    </View>
  );
};

type EditAvatarButtonProps = {
  onPress: () => void;
  type: "user" | "team" | "org";
  children: React.ReactNode;
};

export const EditAvatarButton: React.FC<EditAvatarButtonProps> = ({ onPress, type, children }) => {
  return (
    <Pressable onPress={onPress}>
      {children}
      <View style={[styles.editAvatar, styles[`${type}EditAvatar`]]}>
        <Icon name="edit_pencil" size={14} color={colors.primary[500]} />
      </View>
    </Pressable>
  );
};
